import type { SingleBill } from '@ctct/domain-layer';
import type { SingleBillPayload } from '@ctct/infrastructure-layer';
import type { SingleBillPaymentMethodsPageStore } from '@ctct/state-client-layer';

import { SecurePayCommon } from '../../_shared/PaymentMethods/services/SecurePayCommon';
import type { CreditCardResponse, EcheckResponse } from '../../_shared/PaymentMethods/types';
import {
  getPaymentLogoName,
  transformCreditCardName,
} from '../../_shared/PaymentMethods/utils/transformCreditCardName';
import { singleBillPaymentMethodsEventBus } from '../SingleBillPaymentMethodsEventBus';

export type { CreditCardResponse, EcheckResponse } from '../../_shared/PaymentMethods/types';

export class SecurePayService {
  private common: SecurePayCommon<
    SingleBillPaymentMethodsPageStore,
    Pick<SingleBill, 'getCreditCardInfo' | 'getEcheckInfo'>
  >;

  constructor(
    private pageStore: SingleBillPaymentMethodsPageStore,
    private securePayInfo: Pick<SingleBill, 'getCreditCardInfo' | 'getEcheckInfo'>,
    partnerName: string,
    isAmexIrAffiliate: boolean
  ) {
    this.common = new SecurePayCommon(pageStore, securePayInfo, partnerName, isAmexIrAffiliate);
  }

  public data() {
    return {
      ...this.common.getCommonData(),

      getCreditCardNumberFormatted: () => this.securePayInfo.getCreditCardInfo()?.cardNumber?.replace(/[* ]/g, ''),
      getCreditCardType: () => this.securePayInfo.getCreditCardInfo()?.cardType.displayString,
      getCreditCardName: () =>
        transformCreditCardName(this.securePayInfo.getCreditCardInfo()?.cardType.displayString.toLowerCase()),
      getCreditCardExpirationDate: () =>
        this.common.transformCreditCardExpirationDate(this.securePayInfo.getCreditCardInfo()?.cardExpirationDate),
      getCreditCardLogoName: () => getPaymentLogoName(this.securePayInfo.getCreditCardInfo()?.cardType.displayString),

      getEcheckAccountType: () => this.securePayInfo.getEcheckInfo()?.accountType.displayString,
      getEcheckFormattedAccountNumber: () => this.securePayInfo.getEcheckInfo()?.accountNumber.replace(/[* ]/g, ''),
    };
  }

  public handlers() {
    return {
      ...this.common.getCommonHandlers(),

      /**
       * @returns token
       * @throws validation error
       */
      validateCreditCard: async (): Promise<SingleBillPayload['payment_method']['creditCard']> => {
        const response = (await this.common.getSecurePayInstance().validatePayment()) as CreditCardResponse;

        return {
          cardType: null,
          cardNumber: null,
          cardExpirationDate: null,
          cardSecureCode: null,
          cardHolderName: '',
          validateCardSecureCode: false,
          data: null,
          expired: false,
          tokenized: true,
          cardToken: response.data.id,
        };
      },

      validateEcheck: async (): Promise<SingleBillPayload['payment_method']['echeck']> => {
        const response = (await this.common.getSecurePayInstance().validatePayment()) as EcheckResponse;
        const eCheckData = this.securePayInfo.getEcheckInfo();

        const token = response.data?.id ?? eCheckData?.token;

        if (!token) {
          throw new Error('Echeck payload misses token');
        }

        return {
          accountNumber: null,
          accountType: null,
          bankName: null,
          checkNumber: null,
          nameOnBankAccount: null,
          routingNumber: null,
          token,
          tokenized: true,
        };
      },

      onSecurePayEditClick: () => {
        this.pageStore.setState({
          securePayEdited: true,
          paymentMethodCanBeUpdated: true,
          showSecurePayIframeBlockedMessage: false,
        });
        singleBillPaymentMethodsEventBus.emit('singleBillPaymentMethodCanBeUpdated', true, { source: 'user' });
      },

      onSecurePayCancelEditClick: () => {
        this.pageStore.setState({
          securePayEdited: false,
          paymentMethodCanBeUpdated: false,
          addedCssToIframe: false,
          showSecurePayIframeBlockedMessage: false,
        });
        singleBillPaymentMethodsEventBus.emit('singleBillPaymentMethodCanBeUpdated', false, { source: 'user' });

        this.common.clearTimeoutIds();
      },
    };
  }
}
